import { useCallback, useEffect, useRef, useState } from 'react';
import { playAlertChime } from '../utils/alertSound';
import { showNewOrderNotification } from '../utils/desktopNotification';

export interface NewOrderAlertMessages {
  notificationTitle: string;
  notificationBody: (count: number) => string;
  titleBadge: (count: number) => string;
}

export interface UseNewOrderAlertsOptions {
  /** PENDING order IDs from the latest successful poll; undefined while loading or after a failure. */
  pendingOrderIds: string[] | undefined;
  enabled: boolean;
  messages: NewOrderAlertMessages;
  /**
   * Runs when the OS notification is clicked, in place of `dismiss()`. The
   * notification promises to take the pharmacist to the order, so the caller
   * needs to clear its filters and select the PENDING column too — dismissing
   * the banner alone can leave them on a screen the order is not on.
   */
  onNotificationClick?: () => void;
}

export interface NewOrderAlerts {
  /** Unacknowledged new PENDING orders. Drives the in-page banner. */
  newCount: number;
  /** Restores the tab title. The banner stays. Fired when the tab regains focus. */
  acknowledge: () => void;
  /** Restores the tab title and clears the count. Fired by the banner actions. */
  dismiss: () => void;
}

export function useNewOrderAlerts({
  pendingOrderIds,
  enabled,
  messages,
  onNotificationClick,
}: UseNewOrderAlertsOptions): NewOrderAlerts {
  // Grows monotonically. A set rather than a snapshot comparison is what makes
  // the first load silent and makes search-filtered responses harmless: an
  // order that disappears behind a filter is already remembered, so its return
  // is not a new arrival.
  const seenIds = useRef<Set<string>>(new Set());
  const seeded = useRef(false);
  const baseTitle = useRef<string>(
    typeof document !== 'undefined' ? document.title : '',
  );
  const countRef = useRef(0);
  const [newCount, setNewCount] = useState(0);

  // latest values without re-running the detection effect
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const idsRef = useRef(pendingOrderIds);
  idsRef.current = pendingOrderIds;
  const notificationClickRef = useRef(onNotificationClick);
  notificationClickRef.current = onNotificationClick;

  const acknowledge = useCallback(() => {
    document.title = baseTitle.current;
  }, []);

  const dismiss = useCallback(() => {
    document.title = baseTitle.current;
    countRef.current = 0;
    setNewCount(0);
  }, []);

  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  // Detection. Keyed on the joined ID list so re-renders with identical data
  // do not re-run this.
  const idsKey = pendingOrderIds ? pendingOrderIds.join('|') : null;
  useEffect(() => {
    const ids = idsRef.current;
    if (!ids) return; // failed or in-flight poll: remember nothing

    if (!seeded.current) {
      seeded.current = true;
      ids.forEach((id) => seenIds.current.add(id));
      return; // the first fetch never alerts
    }

    const fresh = ids.filter((id) => !seenIds.current.has(id));
    ids.forEach((id) => seenIds.current.add(id));

    // Track silently while disabled so re-enabling does not dump a backlog.
    if (fresh.length === 0 || !enabled) return;

    const next = countRef.current + fresh.length;
    countRef.current = next;
    setNewCount(next);
    document.title = messagesRef.current.titleBadge(next);

    playAlertChime();
    showNewOrderNotification({
      title: messagesRef.current.notificationTitle,
      // A stable tag means each notification replaces the last, so the one
      // that survives has to speak for every order still waiting — the
      // cumulative count, not just this batch.
      body: messagesRef.current.notificationBody(next),
      onClick: () => {
        const handler = notificationClickRef.current;
        if (handler) handler();
        else dismissRef.current();
      },
    });
  }, [idsKey, enabled]);

  // Turning alerts off means "stop alerting me". Leaving the banner up and the
  // tab badged would keep two of the four channels running.
  useEffect(() => {
    if (enabled) return;
    dismissRef.current();
  }, [enabled]);

  // The pharmacist is looking at the tab again: stop the title from nagging.
  useEffect(() => {
    const handle = () => {
      if (document.visibilityState === 'visible') acknowledge();
    };
    document.addEventListener('visibilitychange', handle);
    window.addEventListener('focus', handle);
    return () => {
      document.removeEventListener('visibilitychange', handle);
      window.removeEventListener('focus', handle);
    };
  }, [acknowledge]);

  // Never leave a badged title behind on a different page.
  useEffect(
    () => () => {
      document.title = baseTitle.current;
    },
    [],
  );

  return { newCount, acknowledge, dismiss };
}
